/**
 * @file meetingEvidence.ts
 * @brief Evidence-anchored playback resolver + the text helpers intel items share.
 *
 * @details
 * Lifted verbatim out of `MeetingDetailView.tsx` (04-08-2026) when the Mindmap
 * tab needed the same resolver: three tabs (Action items / Key points /
 * Decisions) and now the mindmap all turn a stored offset into a playable span,
 * and a second copy of *this* logic in particular would be the exact bug the
 * anchor work fixed — an item playing the wrong audio under a confident clock.
 *
 * Must stay in lockstep with the mobile controller:
 *   FRONTEND/src/screens/MeetingIntelligence/MeetingDetails/hooks/
 *   useMeetingDetailsContent.ts  ("Evidence-Anchored Playback Controller")
 *
 * @dependencies
 * - features/company-meetings/schemas.ts: MeetingDetail (transcript shape)
 *
 * @references
 * - docs/product/meeting-intel-evidence-anchor-plan.md
 * - BACKEND/src/features/boardroom/anchor.service.mjs
 * - features/company-meetings/components/mindmapEvidence.ts (the newest consumer)
 *
 * @author DES
 * @date 04-08-2026
 */

import type { MeetingDetail } from "../schemas";

// Split a free-text attention/risks blob into discrete points. The model
// usually returns one risk per line (sometimes bullet-prefixed); we strip the
// leading marker so each renders as a clean bullet. (08-07-2026)
export function splitRiskLines(raw: string): string[] {
  return raw
    .split(/\r?\n/)
    .map((line) => line.replace(/^\s*[-•*]\s*/, "").trim())
    .filter(Boolean);
}

// ----------------------------------------------------------------------
// Evidence-anchored voice playback (02-08-2026)
// Must stay in lockstep with the mobile controller:
//   FRONTEND/src/screens/MeetingIntelligence/MeetingDetails/hooks/
//   useMeetingDetailsContent.ts  ("Evidence-Anchored Playback Controller")
// See docs/product/meeting-intel-evidence-anchor-plan.md
//
// A stored offset has TWO shapes needing OPPOSITE corrections: a synthesis
// stamp points at the START of the diarized turn (already the beginning of the
// utterance), while a live bookmark second lands AFTER the statement because
// the user taps once they've heard it. The old resolver applied the tap
// correction (-8s) to both, so an AI item played the previous speaker's tail
// and only a few seconds of the actual decision. We now classify the offset
// against the transcript's segment boundaries. (F1/F2 in the anchor plan)
// ----------------------------------------------------------------------

/** Breathing room before/after a resolved span so it never clips a word. */
const VOICE_STAMP_LEAD_IN_SECS = 1.5;
const VOICE_STAMP_TAIL_SECS = 2;
/** A clip shorter than this is unusable; longer than this is a lecture. */
const VOICE_STAMP_MIN_CLIP_SECS = 6;
const VOICE_STAMP_MAX_CLIP_SECS = 45;
/** Reaction delay to look back over for a TAP-shaped offset only. */
const VOICE_STAMP_TAP_LOOKBACK_SECS = 8;
/** How close to a segment start an offset must be to count as segment-shaped. */
const VOICE_STAMP_SEGMENT_START_TOLERANCE_SECS = 1.5;
/** Blind clip width, used only when the meeting has no transcript offsets. */
const VOICE_STAMP_FALLBACK_CLIP_SECS = 12;

/**
 * A padded, classified playback span. `markerSecs` is what the clock label
 * shows — the moment the point was made, never the padded start.
 */
export type VoiceAnchor = {
  startSecs: number;
  endSecs: number;
  markerSecs: number;
  confidence: "exact" | "approx";
};

// Seconds → mm:ss (zero-padded), matching the mobile voice-stamp label.
export function formatClock(totalSecs: number): string {
  const s = Math.max(0, Math.round(totalSecs));
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

// The clock an anchored row shows. A leading "~" is the whole confidence UI:
// it costs no layout, reads the same in every language, and stops an
// approximate span from carrying the authority of a verified one. Mirrors
// resolveTimestampLabel in the mobile hook. (02-08-2026)
export function anchorLabel(anchor: VoiceAnchor): string {
  const clock = formatClock(anchor.markerSecs);
  return anchor.confidence === "approx" ? `~${clock}` : clock;
}

// Sorted segment starts from the diarized transcript. A segment stores only
// `startSecs`, so segment N's end is bounded by segment N+1's start — that
// bound is what stops a fixed 12s clip from cutting a long turn in half.
export function buildSegmentStarts(
  transcript: MeetingDetail["transcript"],
): number[] {
  return (transcript ?? [])
    .map((r) => (typeof r?.startSecs === "number" ? r.startSecs : null))
    .filter((s): s is number => s != null && s >= 0)
    .sort((a, b) => a - b);
}

// Forward-compatible reader for the Slice-2 anchor object the backend will
// attach to each item. Returns null until that ships.
export function readStoredAnchor(raw: unknown): VoiceAnchor | null {
  if (!raw || typeof raw !== "object") return null;
  const a = raw as Record<string, unknown>;
  if (a.confidence === "none") return null;
  const start = Number(a.startSecs);
  const end = Number(a.endSecs);
  if (!Number.isFinite(start) || start < 0) return null;
  if (!Number.isFinite(end) || end <= start) return null;
  return {
    startSecs: Math.max(0, start - VOICE_STAMP_LEAD_IN_SECS),
    endSecs: end + VOICE_STAMP_TAIL_SECS,
    markerSecs: start,
    confidence: a.confidence === "approx" ? "approx" : "exact",
  };
}

// Resolve a stored offset → its evidence span, or null when we genuinely
// cannot locate it. Returning null is deliberate: the previous resolver
// invented a `duration x fraction` offset and played unrelated audio under a
// confident mm:ss label, and its bookmark tier matched decision i to the i-th
// bookmark — wrong, because the AI list is a superset of the tapped moments.
// (F5/F6 in the anchor plan)
export function resolveVoiceAnchor(
  stampSecs: number | null | undefined,
  storedAnchor: unknown,
  segmentStarts: number[],
  totalDuration: number,
): VoiceAnchor | null {
  const fromStored = readStoredAnchor(storedAnchor);
  if (fromStored) return fromStored;

  if (typeof stampSecs !== "number" || !Number.isFinite(stampSecs) || stampSecs < 0) {
    return null;
  }

  let segStart: number | null = null;
  for (const s of segmentStarts) {
    if (s <= stampSecs + 0.001) segStart = s;
    else break;
  }
  let segEnd: number | null = null;
  for (const s of segmentStarts) {
    if (s > stampSecs + 0.001) {
      segEnd = s;
      break;
    }
  }
  if (segEnd == null && segmentStarts.length > 0 && totalDuration > 0) {
    segEnd = totalDuration;
  }

  // Legacy meeting with no transcript offsets: keep the old blind behaviour
  // rather than refusing to play, but mark it approximate.
  if (segStart == null || segEnd == null) {
    const start = Math.max(0, stampSecs - VOICE_STAMP_TAP_LOOKBACK_SECS);
    return {
      startSecs: start,
      endSecs: start + VOICE_STAMP_FALLBACK_CLIP_SECS,
      markerSecs: stampSecs,
      confidence: "approx",
    };
  }

  // Segment-start shaped → the utterance begins HERE; rewinding is exactly
  // what made the previous speaker play instead of the decision.
  if (Math.abs(stampSecs - segStart) <= VOICE_STAMP_SEGMENT_START_TOLERANCE_SECS) {
    return {
      startSecs: Math.max(0, segStart - VOICE_STAMP_LEAD_IN_SECS),
      endSecs: segEnd + VOICE_STAMP_TAIL_SECS,
      markerSecs: segStart,
      confidence: "exact",
    };
  }

  // Mid-segment → tap shaped. Look back over the reaction delay, but never
  // cross out of this speaker's turn into the previous one.
  return {
    startSecs: Math.max(segStart, stampSecs - VOICE_STAMP_TAP_LOOKBACK_SECS),
    endSecs: Math.min(segEnd, stampSecs + VOICE_STAMP_TAIL_SECS),
    markerSecs: stampSecs,
    confidence: "approx",
  };
}

// Clamp a span into a listenable window. The upper bound matters most: a
// diarized turn can run for minutes, and playing all of it is as useless as
// playing 12 seconds of the wrong turn.
export function anchorClipSecs(anchor: VoiceAnchor): number {
  const raw = Math.max(0, anchor.endSecs - Math.max(0, anchor.startSecs));
  return Math.min(
    VOICE_STAMP_MAX_CLIP_SECS,
    Math.max(VOICE_STAMP_MIN_CLIP_SECS, raw),
  );
}

// ----------------------------------------------------------------------
// Decision ↔ anchor pairing (23-08-2026)
//
// `key_decisions` is a plain string[]; its anchors and stamps live in TWO
// PARALLEL ARRAYS addressed by the same index. That coupling breaks the moment
// a renderer drops an entry: `decisions.filter(Boolean).map((d, i) => …[i])`
// silently pairs every decision after the gap with the NEXT one's audio — the
// exact "tap a decision, hear a different one" bug the anchor work was done to
// kill, reintroduced by a filter rather than by a resolver.
//
// So the pairing happens ONCE, here, before anything is dropped. Both the
// Decisions tab and the mindmap evidence index call this; neither indexes the
// parallel arrays itself. Renderers may then filter/sort the result freely,
// because each row carries its own anchor.
// Related: BACKEND anchor.service.mjs, ensure_boardroom_decision_anchors_column.js
// ----------------------------------------------------------------------

/** One decision with the anchor and stamp that belong to IT, not to its row. */
export type PairedDecision = {
  text: string;
  /** Raw stored anchor (may carry `quote`/`source`/provenance flags). */
  anchorRaw: unknown;
  stampSecs: number | null;
  /** Its position in `key_decisions` (before blanks are dropped): the
   *  decision's id for B50 export / Share. */
  index: number;
};

/**
 * Pair each decision with its own anchor/stamp, then drop empty entries.
 * Order is preserved; the index of the RESULT is a render position and must
 * never be used to look anything up again.
 */
export function pairDecisions(meeting: {
  key_decisions?: string[] | null;
  key_decision_anchors?: unknown[] | null;
  key_decision_stamps?: (number | null)[] | null;
}): PairedDecision[] {
  const anchors = meeting.key_decision_anchors ?? [];
  const stamps = meeting.key_decision_stamps ?? [];
  return (meeting.key_decisions ?? [])
    .map((text, i) => ({
      text: String(text ?? ""),
      anchorRaw: anchors[i] ?? null,
      stampSecs:
        typeof stamps[i] === "number" && Number.isFinite(stamps[i] as number)
          ? (stamps[i] as number)
          : null,
      index: i,
    }))
    .filter((d) => !!d.text.trim());
}
